/**
 * The **tab strip** primitive (Chassis Slice H, PRD #534 D7 / ADR-0271): the
 * pressed-state + switch bookkeeping the leaderboard window's five board tabs
 * (Online Time | Gold | Duels | Storm Marks | Arena) hand-rolled per button
 * (Rat Swarm Slice F onward). Each tab is a `<button>` the consumer already owns
 * (or built); the primitive keeps `aria-pressed` and the `.active` styling hook
 * in sync and fires `onSwitch` only on a real change - a same-tab click is a
 * no-op, exactly the old `setBoard` guard.
 *
 * The primitive also supplies the shared old-world visual hooks: `.ui-tab` on
 * every present button and `.ui-tabs` on their common parent. Consumers own
 * labels and content, while one stylesheet owns the tab chrome.
 *
 * `set` selects silently (no `onSwitch`), for the every-open "return to the
 * default tab" reset that refetches through its own path.
 */

/** One tab: its button (tolerated absent, so partial markup never throws) and value. */
export interface TabSpec<V extends string> {
  button: HTMLElement | null;
  value: V;
}

export interface TabsHandle<V extends string> {
  /** The selected value. */
  value(): V;
  /** Select `v` silently (pressed-state sync only, no onSwitch) - the open-reset path. */
  set(v: V): void;
}

/** Wire a set of tab buttons; clicks switch tabs and fire `onSwitch` on a real change. */
export function createTabs<V extends string>(
  tabs: readonly TabSpec<V>[],
  initial: V,
  onSwitch: (v: V) => void,
): TabsHandle<V> {
  let current = initial;

  const buttons = tabs.flatMap((tab) => (tab.button ? [tab.button] : []));
  for (const button of buttons) button.classList.add('ui-tab');
  const parent = buttons[0]?.parentElement;
  if (parent && buttons.every((button) => button.parentElement === parent)) {
    parent.classList.add('ui-tabs');
  }

  /** Reflect the selected tab on every button (pressed state + styling hook). */
  const sync = (): void => {
    for (const tab of tabs) {
      tab.button?.setAttribute('aria-pressed', String(current === tab.value));
      tab.button?.classList.toggle('active', current === tab.value);
    }
  };

  for (const tab of tabs) {
    tab.button?.addEventListener('click', () => {
      if (current === tab.value) return;
      current = tab.value;
      sync();
      onSwitch(tab.value);
    });
  }
  sync();

  return {
    value: () => current,
    set(v: V) {
      current = v;
      sync();
    },
  };
}
